import { Box } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { PasswordInput } from '../../../../core-components/theme/components/Fields';

import type { NewPasswordErrors, NewPasswordField, NewPasswordValues, PasswordRuleState } from '../../domain/newPasswordRules';

import { PasswordRuleLine } from './PasswordRuleLine';

const fieldsSx: SystemStyleObject<Theme> = { display: 'flex', flexDirection: 'column', gap: 4 };
const repeatBlockSx: SystemStyleObject<Theme> = { display: 'flex', flexDirection: 'column', gap: 2 };

export type NewPasswordRefs = Readonly<Record<NewPasswordField, RefObject<HTMLInputElement>>>;

export interface NewPasswordFieldsProps {
  readonly values: NewPasswordValues;
  readonly errors: NewPasswordErrors;
  readonly rule: PasswordRuleState;
  readonly minLength: number;
  readonly refs: NewPasswordRefs;
  readonly ruleId: string;
  readonly readOnly?: boolean;
  readonly autoFocus?: boolean;
  readonly onChange: (field: NewPasswordField, value: string) => void;
  readonly onBlur: (field: NewPasswordField) => void;
  readonly onSubmit: () => void;
}

const onEnterKey =
  (action: () => void) =>
  (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    action();
  };

export const NewPasswordFields: React.FC<NewPasswordFieldsProps> = ({
  values,
  errors,
  rule,
  minLength,
  refs,
  ruleId,
  readOnly = false,
  autoFocus = false,
  onChange,
  onBlur,
  onSubmit,
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={fieldsSx}>
      <PasswordInput
        inputRef={refs.next}
        label={t('passcodeAuth.passwordForm.new', 'New password')}
        autoComplete="new-password"
        size="medium"
        required
        hideRequiredMark
        autoFocus={autoFocus}
        readOnly={readOnly}
        value={values.next}
        onValueChange={(value) => onChange('next', value)}
        onBlur={() => onBlur('next')}
        onKeyDown={onEnterKey(() => refs.confirm.current?.focus())}
        inputProps={{ enterKeyHint: 'next' }}
        error={errors.next !== null}
        describedById={ruleId}
      />
      <Box sx={repeatBlockSx}>
        <PasswordInput
          inputRef={refs.confirm}
          label={t('passcodeAuth.passwordForm.repeat', 'Repeat password')}
          autoComplete="new-password"
          size="medium"
          required
          hideRequiredMark
          readOnly={readOnly}
          value={values.confirm}
          onValueChange={(value) => onChange('confirm', value)}
          onBlur={() => onBlur('confirm')}
          onKeyDown={onEnterKey(onSubmit)}
          inputProps={{ enterKeyHint: 'done' }}
          error={errors.confirm !== null}
          describedById={ruleId}
        />
        <PasswordRuleLine id={ruleId} state={rule} minLength={minLength} />
      </Box>
    </Box>
  );
};
